import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api, apiErr } from "@/lib/api";

const PENDING_KEY = "runebox_pending_invite";

// Landing page for an org invite email link (F2). Not behind ProtectedRoute — an invitee may
// not have an account yet, so this page itself handles the "please sign in" branch.
export default function OrgJoin() {
  const { user, loading: authLoading, login } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking"); // checking | need-login | joining | error
  const [errMsg, setErrMsg] = useState("");
  const token = params.get("token");

  useEffect(() => {
    if (token) localStorage.setItem(PENDING_KEY, token);
  }, [token]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setStatus("need-login");
      return;
    }
    const t = token || localStorage.getItem(PENDING_KEY);
    if (!t) {
      setStatus("error");
      setErrMsg("This invite link is missing its token.");
      return;
    }
    setStatus("joining");
    api
      .post("/orgs/invites/accept", { token: t })
      .then(({ data }) => {
        localStorage.removeItem(PENDING_KEY);
        toast.success(`You're in — welcome to ${data.name}`);
        navigate(`/orgs/${data.slug}`, { replace: true });
      })
      .catch((e) => {
        setStatus("error");
        setErrMsg(apiErr(e, "Could not accept this invite."));
      });
  }, [user, authLoading, token, navigate]);

  const redirectTarget = `/orgs/join${token ? `?token=${token}` : ""}`;

  return (
    <AppShell>
      <div className="mx-auto max-w-md text-center">
        {status === "checking" || status === "joining" ? (
          <div className="mt-16 flex flex-col items-center gap-3 text-[color:var(--rb-text-faint)]">
            <Loader2 className="h-6 w-6 animate-spin" />
            <p className="text-sm">{status === "joining" ? "Joining the org…" : "Checking your invite…"}</p>
          </div>
        ) : status === "need-login" ? (
          <div className="mt-16 space-y-4">
            <p className="text-sm text-[color:var(--rb-text-dim)]">Sign in or create an account to accept this invite.</p>
            <div className="flex justify-center gap-3">
              <Button
                onClick={() => login(redirectTarget)}
                data-testid="org-join-google-button"
                className="rounded-xl bg-[color:var(--rb-accent)] text-[color:var(--rb-void)] hover:bg-[color:var(--rb-accent-2)]"
              >
                Continue with Google
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate(`/login?redirect=${encodeURIComponent(redirectTarget)}`)}
                data-testid="org-join-email-button"
                className="rounded-xl border-[color:var(--rb-border)]"
              >
                Sign in with email
              </Button>
            </div>
          </div>
        ) : (
          <div className="mt-16 space-y-2">
            <p className="text-sm text-[color:#f87171]" data-testid="org-join-error">{errMsg}</p>
            <Button variant="ghost" onClick={() => navigate("/")} className="text-[color:var(--rb-text-faint)]">Back home</Button>
          </div>
        )}
      </div>
    </AppShell>
  );
}
